import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ArticleOutlined,
  GroupOutlined,
  PersonOffOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, Divider, Link, Stack, Toolbar, Typography } from '@mui/material';
import { useDownloadExcel } from 'react-export-table-to-excel';

import { convertPDF, downloadFile } from '../../hooks/Helper';
import { IMenuOption } from '../../interfaces/IMenuOption';
import { RootState, useTypedSelector } from '../../redux/store';
import DropdownButton from '../DropdownButton';

const collaborateMenu: IMenuOption[] = [
  {
    value: 'collaborating',
    label: 'Collaborating',
    startIcon: <GroupOutlined />,
  },
  {
    value: 'editing',
    label: 'Editing',
    startIcon: <PersonOffOutlined />,
  },
];

const downloadMenu: IMenuOption[] = [
  {
    value: 'pdf',
    label: 'PDF',
    startIcon: <PictureAsPdfOutlined />,
  },
  {
    value: 'wordDocument',
    label: 'Word Document',
    startIcon: <ArticleOutlined />,
  },
  {
    value: 'insights',
    label: 'Insights as Excel',
    startIcon: <ArticleOutlined />,
  },
];

interface Props {
  canDownload?: boolean;
  collaborating?: boolean;
  goBackUrl?: string;
  renderAction?: () => React.ReactNode;
  setCanEdit?: Dispatch<SetStateAction<boolean>>;
  collaboratorDisabled?: boolean;
  canEdit?: boolean;
}

const DraftHeader: React.FC<Props> = ({
  collaborating = false,
  canDownload = false,
  renderAction,
  goBackUrl,
  setCanEdit,
  collaboratorDisabled = false,
  canEdit = false,
}) => {
  const [collaboratingValue, setCollaboratingValue] = useState<string>();

  const { fileLink, draft, instance } = useTypedSelector(
    (state: RootState) => state.draft
  );

  useEffect(() => {
    if (collaboratingValue === 'Editing') {
      setCanEdit?.(true);
    } else {
      setCanEdit?.(false);
    }
  }, [collaboratingValue, setCanEdit]);

  const fileName = useMemo(() => {
    const file_name_array = draft?.name.split('.');
    return file_name_array?.[0] || '';
  }, [draft]);

  const insightsTable = document.getElementById('table-to-xls-insights');

  const { onDownload } = useDownloadExcel({
    currentTableRef: insightsTable,
    filename: `${fileName}-insights`,
    sheet: 'Insights',
  });

  const handleDownload = React.useCallback(
    async (value: string) => {
      if (!instance) return;
      if (value === 'pdf') {
        const { Core } = instance;
        if (fileLink) {
          const file = await convertPDF(Core, fileLink);
          downloadFile(file, draft?.name, 'pdf');
        }
      } else if (value === 'wordDocument') {
        const { Core } = instance;

        const doc = Core.documentViewer.getDocument();
        const annotationManager = Core.documentViewer.getAnnotationManager();
        const xfdfString = await annotationManager.exportAnnotations();
        const data = await doc.getFileData({
          // saves the document with annotations in it
          xfdfString,
        });
        const arr = new Uint8Array(data);
        const blob = new Blob([arr], { type: 'application/docx' });
        downloadFile(blob, draft?.name, 'docx');
      } else {
        if (insightsTable) {
          onDownload();
        }
      }
    },
    [fileLink, insightsTable, onDownload]
  );

  return (
    <Box>
      <Toolbar>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {goBackUrl && (
              <Link
                href={goBackUrl}
                color="riPrimary.500"
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <ArrowBackIcon fontSize="small" sx={{ marginRight: '2px' }} />
                Close
              </Link>
            )}
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            flex={1}
            justifyContent="center"
          >
            <Typography
              marginRight={4}
              fontSize="16px"
              fontWeight="500"
              whiteSpace="nowrap"
            >
              {fileName}
            </Typography>
            {collaborating && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box marginLeft={2}>
                  <DropdownButton
                    changeButtonText
                    options={collaborateMenu}
                    setValue={setCollaboratingValue}
                    disabled={collaboratorDisabled}
                    buttonName={canEdit ? 'Editing' : 'Collaborating'}
                  />
                </Box>
              </>
            )}
          </Stack>
          <Stack direction="row" flex={1} justifyContent="flex-end">
            {canDownload && (
              <DropdownButton
                options={downloadMenu}
                startIcon={<SaveAltIcon />}
                menuHeader="Download as:"
                buttonName="Download"
                handleDropdownChange={handleDownload}
              />
            )}
            {renderAction?.()}
          </Stack>
        </Stack>
      </Toolbar>
      <Divider />
    </Box>
  );
};

export default DraftHeader;
